import React, { useState } from 'react';
import { Calendar, Clock, CheckCircle, XCircle, AlertCircle, PieChart, Book } from 'lucide-react';

// Sample attendance data structure
const attendanceData = {
  1: {
    courseId: 1,
    courseName: "Math 101",
    courseCode: "MTH101",
    instructor: "Dr. Sarah Johnson",
    totalClasses: 45,
    attendedClasses: 42,
    lastUpdated: "2024-11-14",
    schedule: "Mon, Wed, Fri - 10:00 AM",
    monthlyAttendance: {
      "October": { present: 15, absent: 1, total: 16 },
      "September": { present: 14, absent: 2, total: 16 },
      "August": { present: 13, absent: 0, total: 13 }
    },
    allClasses: [
      { date: "2024-11-14", status: "present", topic: "Integration Methods", time: "10:00 AM" },
      { date: "2024-11-12", status: "present", topic: "Differential Equations", time: "10:00 AM" },
      { date: "2024-11-10", status: "absent", topic: "Complex Numbers", time: "10:00 AM" },
      { date: "2024-11-08", status: "present", topic: "Vector Calculus", time: "10:00 AM" },
      { date: "2024-11-06", status: "present", topic: "Linear Algebra", time: "10:00 AM" },
      { date: "2024-11-04", status: "present", topic: "Matrices", time: "10:00 AM" },
      { date: "2024-11-02", status: "absent", topic: "Determinants", time: "10:00 AM" }
    ]
  },
  
  2: {
    courseId: 2,
    courseName: "Physics 101",
    courseCode: "PHY101",
    instructor: "Dr. Robert Brown",
    totalClasses: 40,
    attendedClasses: 38,
    lastUpdated: "2024-11-14",
    schedule: "Tue, Thu - 11:00 AM",
    monthlyAttendance: {
      "October": { present: 14, absent: 2, total: 16 },
      "September": { present: 15, absent: 1, total: 16 },
      "August": { present: 12, absent: 1, total: 13 }
    },
    allClasses: [
      { date: "2024-11-14", status: "present", topic: "Quantum Mechanics", time: "11:00 AM" },
      { date: "2024-11-12", status: "present", topic: "Wave Theory", time: "11:00 AM" },
      { date: "2024-11-10", status: "present", topic: "Thermodynamics", time: "11:00 AM" },
      { date: "2024-11-08", status: "absent", topic: "Nuclear Physics", time: "11:00 AM" },
      // Add more classes as needed
    ]
  }
  // Add more courses with similar structure
};

function CoursesAttendance() {
  const [expandedCourse, setExpandedCourse] = useState(null);

  const getAttendancePercentage = (present, total) => {
    return ((present / total) * 100).toFixed(1);
  };

  const getAttendanceColor = (percentage) => {
    if (percentage >= 90) return 'bg-green-100 text-green-800';
    if (percentage >= 75) return 'bg-blue-100 text-blue-800';
    return 'bg-red-100 text-red-800';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'text-green-500';
      case 'absent': return 'text-red-500';
      default: return 'text-yellow-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'absent': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md border-t-4 border-blue-500">
        <div className="p-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-500" />
            Course Attendance Overview
          </h1>
          <p className="text-gray-600 mt-2">Track your attendance across all courses</p>
        </div>
      </div>

      {/* Course Cards */}
      <div className="grid grid-cols-1 gap-6">
        {Object.entries(attendanceData).map(([id, course]) => {
          const attendancePercentage = getAttendancePercentage(course.attendedClasses, course.totalClasses);
          const colorClass = getAttendanceColor(attendancePercentage);

          return (
            <div key={id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="p-6">
                {/* Course Header */}
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-xl font-bold">{course.courseName}</h2>
                    <p className="text-gray-600">{course.courseCode}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
                    {attendancePercentage}% Attendance
                  </span>
                </div>

                {/* Basic Info and Stats */}
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Book className="w-4 h-4" />
                      <span>{course.instructor}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{course.schedule}</span>
                    </div>
                  </div>

                  <div className="col-span-2 grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Total Classes</p>
                      <p className="text-xl font-bold text-blue-600">{course.totalClasses}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Present</p>
                      <p className="text-xl font-bold text-green-600">{course.attendedClasses}</p>
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Absent</p>
                      <p className="text-xl font-bold text-red-600">
                        {course.totalClasses - course.attendedClasses}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Monthly Breakdown */}
                <div className="space-y-4 mb-6">
                  <h3 className="text-lg font-semibold">Monthly Breakdown</h3>
                  {Object.entries(course.monthlyAttendance).map(([month, data]) => (
                    <div key={month} className="border-b pb-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{month}</span>
                        <span className="text-sm text-gray-600">
                          {getAttendancePercentage(data.present, data.total)}% Attendance
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-500 rounded-full h-2"
                          style={{ width: `${(data.present / data.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* All Classes Table */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">All Classes</h3>
                  <div className="overflow-auto max-h-[300px] rounded-lg border"> {/* Height set to show ~5 rows */}
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Time</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Topic</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {course.allClasses.map((class_, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{class_.date}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{class_.time}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                {getStatusIcon(class_.status)}
                                <span className={`text-sm ${getStatusColor(class_.status)}`}>
                                  {class_.status.charAt(0).toUpperCase() + class_.status.slice(1)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{class_.topic}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CoursesAttendance;